import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import styles from './AccountConfiguration.module.css';
import StepProgress from '../../../components/progression/StepProgress';
import EmailButton from '../../../components/auth/EmailButton';
import createUserConf from '../../../apiServices/accountConfiguration/accountConfiguration';
import SingleLineTextField from '../../../components/fieldTypes/SingleLineTextField';
import { checkPseudo } from '../../../apiServices/auth/email';
import ThemeButton from '../../../components/buttons/ThemeButton';

function isValidPseudo(pseudo) {
  const regex = /^([a-zA-Zàâäéèêëîïôöùûüç0-9@.]{5,})#([a-zA-Z0-9]{4,})$/;
  return regex.test(pseudo);
}

function isValidWorkspaceName(workspaceName) {
  return /^[a-zA-Z0-9]{3,}$/.test(workspaceName);
}

function AccountConfiguration() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [isTransition, setIsTransition] = useState(false);
  const stepNumber = 3;
  const [workspaceName, setWorkspaceName] = useState('');
  const [pseudo, setPseudo] = useState('');
  const navigate = useNavigate();

  const handleCreateUserConf = async () => {
    const response = await createUserConf({ theme: 'dark', workspaceName, pseudo });
    if (response?.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('firstTime');
      navigate('/login');
    }
    if (response?.status === 200) {
      localStorage.removeItem('firstTime');
      navigate('/workspaces');
    }
  };
  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    >
      <div
        className={styles.glassEffect}
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          height: '300px',
          width: '320px',
          gap: '5px',
          boxSizing: 'border-box',
        }}
      >
        <AnimatePresence>
          {(currentStep === 1 && !isTransition) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                height: '270px',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '270px',
              }}
            >
              <h3 style={{
                color: 'var(--very-visible)',
                marginBottom: '0px',
              }}
              >
                {t('accountConfiguration.choosePseudo')}
              </h3>
              <SingleLineTextField
                value={pseudo}
                setValue={setPseudo}
                placeholder={t('accountConfiguration.pseudoPlaceholder')}
                isControlled
                width="100%"
                rule={isValidPseudo}
                helperMessage={t('accountConfiguration.pseudoRuleHelper')}
              />

              <EmailButton
                text={t('accountConfiguration.continue')}
                onClick={async () => {
                  const response = await checkPseudo({ pseudo });
                  if (response.data) {
                    setIsTransition(true);
                    setCurrentStep(2);
                    setTimeout(() => setIsTransition(false), 200);
                  }
                }}
                width="100%"
              />
            </motion.div>
          )}
          {(currentStep === 2 && !isTransition) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              height: '270px',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '270px',
              boxSizing: 'border-box',
            }}
          >
            <h3 style={{
              color: 'var(--very-visible)',
              marginBottom: '0px',
              textAlign: 'center',
            }}
            >
              {t('accountConfiguration.chooseTheme')}
            </h3>
            <div style={{
              marginTop: '5px',
              marginBottom: '13px',
            }}
            >
              <ThemeButton height="40px" width="40px" />
            </div>
            <EmailButton
              text={t('common.cancel')}
              onClick={() => {
                setIsTransition(true);
                setCurrentStep(1);
                setTimeout(() => setIsTransition(false), 200);
              }}
              width="100%"
            />
            <EmailButton
              text={t('accountConfiguration.continue')}
              onClick={() => {
                setIsTransition(true);
                setCurrentStep(3);
                setTimeout(() => setIsTransition(false), 200);
              }}
              width="100%"
            />
          </motion.div>
          )}
          {(currentStep === 3 && !isTransition) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              height: '270px',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >

            <p style={{
              marginTop: '15px',
              marginBottom: '5px',
              marginLeft: '10px',
              marginRight: '10px',
              width: '230px',
              display: 'flex',
              justifyContent: 'center',
              color: 'var(--very-visible)',
              textAlign: 'center',
            }}
            >
              {t('accountConfiguration.chooseWorkspaceName')}
            </p>
            <SingleLineTextField
              value={workspaceName}
              setValue={(e) => {
                const isValid = /^[a-zA-Z0-9]+$/.test(e); // lettres et chiffres uniquement
                if (isValid || e === '') {
                  setWorkspaceName(e);
                }
              }}
              isControlled
              placeholder={t('accountConfiguration.workspaceNamePlaceholder')}
              rule={isValidWorkspaceName}
              helperMessage={t('accountConfiguration.workspaceNameRuleHelper')}
            />
            <EmailButton
              text={t('common.cancel')}
              onClick={() => {
                setIsTransition(true);
                setCurrentStep(2);
                setTimeout(() => setIsTransition(false), 200);
              }}
              width="100%"
            />
            <EmailButton
              text={t('common.confirm')}
              disabled={workspaceName.length < 3}
              onClick={handleCreateUserConf}
              width="100%"
            />
          </motion.div>
          )}
        </AnimatePresence>
        <StepProgress length={stepNumber} currentStep={currentStep} />
      </div>
    </div>
  );
}
export default AccountConfiguration;
